import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import { useAction } from "wasp/client/operations";
import { addRecipeToMealPlan } from "wasp/client/operations";
import { toast } from "sonner";
import { CustomRecipePayload } from "../../types/customRecipe";
import CreateCustomRecipePageView from './CreateCustomRecipePage.view';

// Business logic types
interface Ingredient {
  name: string;
  amount: number;
  unit: string;
  category?: string;
  optional?: boolean;
}

interface CreateCustomRecipePageContainerProps {
  // Optional props for customization
}

/**
 * CreateCustomRecipePage Container - Centralizes all data logic, hooks, and business logic
 * 
 * This centralizes:
 * - Wasp operations and data fetching (1 action)
 * - Multi-step form state management (4 steps)
 * - Form validation logic and error handling
 * - Recipe creation and submission logic
 * - Ingredients, instructions, and dietary tags management
 * - Navigation and step progression logic
 * - All event handlers and business logic
 */
export default function CreateCustomRecipePageContainer({}: CreateCustomRecipePageContainerProps) {
  // Wasp hooks for data fetching and actions
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const isAuthenticated = !!user;
  const addRecipeAction = useAction(addRecipeToMealPlan);

  // Business logic state - Multi-step form management
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Business logic state - Recipe form data
  const [formData, setFormData] = useState<Partial<CustomRecipePayload>>({
    title: "",
    description: "",
    servings: 2,
    prepTime: 15,
    cookTime: 30,
    cuisine: "",
    difficulty: "medium",
    dietaryTags: [],
    ingredients: [{ name: "", amount: 1, unit: "cup", category: "Other" }],
    instructions: [""],
    nutrition: {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      fiber: 0,
      sugar: 0,
    },
    imageUrl: "",
    tips: [],
  });

  // Business logic: Form data management
  const updateFormData = (field: keyof CustomRecipePayload, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Business logic: Ingredients management
  const addIngredient = () => {
    const ingredients = [
      ...(formData.ingredients || []),
      { name: "", amount: 1, unit: "cup", category: "Other" },
    ];
    updateFormData("ingredients", ingredients);
  };

  const updateIngredient = (
    index: number,
    field: keyof Ingredient,
    value: any,
  ) => {
    const ingredients = [...(formData.ingredients || [])];
    ingredients[index] = { ...ingredients[index], [field]: value };
    updateFormData("ingredients", ingredients);
  };

  const removeIngredient = (index: number) => {
    const ingredients = (formData.ingredients || []).filter(
      (_, i) => i !== index,
    );
    updateFormData("ingredients", ingredients);
  };

  // Business logic: Instructions management
  const addInstruction = () => {
    const instructions = [...(formData.instructions || []), ""];
    updateFormData("instructions", instructions);
  };

  const updateInstruction = (index: number, text: string) => {
    const instructions = [...(formData.instructions || [])];
    instructions[index] = text;
    updateFormData("instructions", instructions);
  };

  const removeInstruction = (index: number) => {
    const instructions = (formData.instructions || []).filter(
      (_, i) => i !== index,
    );
    updateFormData("instructions", instructions);
  };

  // Business logic: Dietary tags management
  const addDietaryTag = (tag: string) => {
    const tags = [...(formData.dietaryTags || [])];
    if (!tags.includes(tag)) {
      tags.push(tag);
      updateFormData("dietaryTags", tags);
    }
  };

  const removeDietaryTag = (tag: string) => {
    const tags = (formData.dietaryTags || []).filter((t) => t !== tag);
    updateFormData("dietaryTags", tags);
  };

  // Business logic: Form validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title?.trim()) {
      newErrors.title = "Recipe title is required";
    }

    if (!formData.description?.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.servings || formData.servings < 1) {
      newErrors.servings = "Servings must be at least 1";
    }

    if (!formData.prepTime || formData.prepTime < 0) {
      newErrors.prepTime = "Prep time must be 0 or greater";
    }

    if (!formData.cookTime || formData.cookTime < 0) {
      newErrors.cookTime = "Cook time must be 0 or greater";
    }

    // Validate ingredients
    const ingredients = formData.ingredients || [];
    if (ingredients.length === 0) {
      newErrors.ingredients = "At least one ingredient is required";
    } else {
      ingredients.forEach((ingredient, index) => {
        if (!ingredient.name?.trim()) {
          newErrors[`ingredient_${index}_name`] = "Ingredient name is required";
        }
        if (!ingredient.amount || ingredient.amount <= 0) {
          newErrors[`ingredient_${index}_amount`] = "Amount must be greater than 0";
        }
      });
    }

    // Validate instructions
    const instructions = formData.instructions || [];
    if (instructions.length === 0 || !instructions.some(inst => inst.trim())) {
      newErrors.instructions = "At least one instruction is required";
    } else {
      instructions.forEach((instruction, index) => {
        if (!instruction?.trim()) {
          newErrors[`instruction_${index}`] = "Instruction cannot be empty";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Business logic: Step navigation
  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step: number) => {
    if (step >= 1 && step <= 4) {
      setCurrentStep(step);
    }
  };

  // Business logic: Form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    if (!isAuthenticated) {
      toast.error("You must be logged in to create recipes");
      return;
    }

    setIsSubmitting(true);
    try {
      // Transform form data to match expected format
      const customRecipe: CustomRecipePayload = {
        title: formData.title!,
        description: formData.description!,
        servings: formData.servings!,
        prepTime: formData.prepTime!,
        cookTime: formData.cookTime!,
        difficulty: formData.difficulty!,
        cuisine: formData.cuisine || "",
        dietaryTags: formData.dietaryTags || [],
        ingredients: formData.ingredients || [],
        instructions: formData.instructions?.filter(inst => inst.trim()) || [],
        nutrition: formData.nutrition!,
        imageUrl: formData.imageUrl || "",
        tips: formData.tips || [],
      };

      const result = await addRecipeAction({
        customRecipe,
        recipeId: `custom-${Date.now()}`,
        dayIndex: 0,
        mealType: "dinner",
        servings: customRecipe.servings,
      });

      if (result && (result as any).success) {
        toast.success("Recipe created and added to your meal plan!");
        navigate("/plans");
      } else {
        throw new Error("Failed to create recipe");
      }
    } catch (error) {
      console.error("Failed to create custom recipe:", error);
      toast.error("Failed to create recipe. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Business logic: Navigation handlers
  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoToPlans = () => {
    navigate("/plans");
  };

  // Step configuration
  const steps = [
    { id: 1, title: "Basic Info", description: "Recipe details and timing" },
    { id: 2, title: "Ingredients", description: "What you'll need" },
    { id: 3, title: "Instructions", description: "How to make it" },
    { id: 4, title: "Review", description: "Double-check everything" },
  ];

  // Computed values
  const currentStepData = steps.find((s) => s.id === currentStep);
  const canProceed = currentStep < steps.length;
  const canGoBack = currentStep > 1;
  const isLastStep = currentStep === steps.length;

  // Common data constants
  const commonUnits = [
    "cup", "cups", "tbsp", "tsp", "oz", "lb", "g", "kg", "ml", "l",
    "piece", "pieces", "clove", "cloves", "bunch", "bunches", 
    "can", "cans", "package", "packages", "slice", "slices",
  ];

  const commonCategories = [
    "Produce", "Meat", "Dairy", "Grains", "Pantry", "Spices", "Oils", "Other",
  ];

  const commonCuisines = [
    "American", "Italian", "Mexican", "Asian", "Mediterranean", "Indian", 
    "French", "Thai", "Chinese", "Japanese", "Greek", "Spanish", "Other",
  ];

  const difficultyOptions = [
    { value: "easy", label: "Easy" },
    { value: "medium", label: "Medium" },
    { value: "hard", label: "Hard" },
  ];

  const commonDietaryTags = [
    "Vegetarian", "Vegan", "Gluten-Free", "Dairy-Free", "Low-Carb", 
    "High-Protein", "Keto", "Paleo", "Low-Sodium", "Heart-Healthy",
  ];

  // Map all props to the view
  const viewProps = {
    // Data
    formData,
    currentStep,
    isSubmitting,
    errors,
    steps,
    currentStepData,
    isAuthenticated,
    
    // Computed values
    canProceed,
    canGoBack,
    isLastStep,
    
    // Constants/Options
    commonUnits,
    commonCategories,
    commonCuisines,
    difficultyOptions,
    commonDietaryTags,
    
    // Event handlers - Form data
    onUpdateFormData: updateFormData,
    
    // Event handlers - Ingredients
    onAddIngredient: addIngredient,
    onUpdateIngredient: updateIngredient,
    onRemoveIngredient: removeIngredient,
    
    // Event handlers - Instructions
    onAddInstruction: addInstruction,
    onUpdateInstruction: updateInstruction,
    onRemoveInstruction: removeInstruction,
    
    // Event handlers - Dietary tags
    onAddDietaryTag: addDietaryTag,
    onRemoveDietaryTag: removeDietaryTag,
    
    // Event handlers - Navigation
    onNextStep: nextStep,
    onPrevStep: prevStep,
    onGoToStep: goToStep,
    onGoBack: handleGoBack,
    onGoToPlans: handleGoToPlans,
    
    // Event handlers - Form actions
    onValidateForm: validateForm,
    onSubmit: handleSubmit,
  };

  return <CreateCustomRecipePageView {...viewProps} />;
}

// Export types for the view component
export type { Ingredient };